import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditIcon from '@mui/icons-material/Edit'
import LinkIcon from '@mui/icons-material/Link'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import type { IQuickInstrumentListItem } from '@brushfire/types'
import calendarEventFillIcon from '@/assets/campaigns/direct-instrument/calendar-event-fill.svg'
import dashboardFillIcon from '@/assets/campaigns/direct-instrument/dashboard-fill.svg'
import pauseFillIcon from '@/assets/campaigns/direct-instrument/pause-fill.svg'
import stopFillIcon from '@/assets/campaigns/direct-instrument/stop-fill.svg'
import {
  instrumentBuilderPath,
  instrumentDashboardPath,
  targetAudiencePath,
} from '@/features/instruments/lib'
import { libraryCardHoverSx } from '@/shared/lib/libraryCardHover'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  DIRECT_INSTRUMENT_STATUS_PILL,
  displayCreatedBy,
  formatCreatedDate,
  formatUsdAmount,
  mapApiStatusToUiStatus,
  pauseReasonLabel,
  resolveBudgetUsedUsd,
  type DirectInstrumentUiStatus,
} from '../lib/quick-instrument-display'
import { resolveSignupSurveyShareUrl } from '../lib/signup-survey-share-url'

const PRIMARY = '#2563EB'
const SUCCESS = '#16A34A'
const WARNING = '#D97706'
const TITLE = '#0f172a'
const META_LABEL = '#04183f'
const CREATED_DATE = '#3b82f6'
const CREATED_BY = '#0d9467'
const LINK_COPIED_RESET_MS = 2000
const SIGN_UP_SURVEY_LABEL = 'Sign-up Survey'
const REGULAR_SURVEY_LABEL = 'Regular Survey'

const METRIC_TILES = [
  {
    key: 'budget',
    label: 'Budget Used',
    bgcolor: '#f0f4fd',
    borderColor: '#c7ebf9',
  },
  {
    key: 'reward',
    label: 'Per Reward',
    bgcolor: '#f6f1fe',
    borderColor: '#ffd9ea',
  },
  {
    key: 'responses',
    label: 'Total Responses',
    bgcolor: '#eaf3ec',
    borderColor: '#d3fadc',
  },
] as const

const PAUSE_REASON_PILL = {
  border: 'rgba(37, 99, 235, 0.3)',
  bg: 'rgba(37, 99, 235, 0.08)',
  color: PRIMARY,
}

const STATUS_PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 26,
  borderRadius: '999px',
  borderStyle: 'solid',
  borderWidth: '1px',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  px: '18px',
  whiteSpace: 'nowrap',
} as const

const ACTION_BUTTON_SX = {
  width: 26,
  height: 26,
  p: 0,
  borderRadius: '5px',
  flexShrink: 0,
} as const

type Props = Readonly<{
  item: IQuickInstrumentListItem
  canMutate: boolean
  onPause: (item: IQuickInstrumentListItem) => void
  onStop: (item: IQuickInstrumentListItem) => void
  onResume: (item: IQuickInstrumentListItem) => void
  /** BFP-1142 — edit a Live / Paused survey (pencil); hidden when omitted. */
  onEditLive?: (item: IQuickInstrumentListItem) => void
}>

function ActionIcon({
  label,
  tone,
  onClick,
  children,
  testId,
}: Readonly<{
  label: string
  tone: { bg: string; color: string }
  onClick: () => void
  children: ReactNode
  testId: string
}>) {
  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={onClick}
        data-testid={testId}
        size="small"
        sx={{
          ...ACTION_BUTTON_SX,
          bgcolor: tone.bg,
          color: tone.color,
          '&:hover': { bgcolor: tone.bg, opacity: 0.85 },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  )
}

function FigmaActionIcon({
  label,
  bg,
  src,
  width,
  height,
  onClick,
  testId,
}: Readonly<{
  label: string
  bg: string
  src: string
  width: number
  height: number
  onClick: () => void
  testId: string
}>) {
  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={onClick}
        data-testid={testId}
        size="small"
        sx={{
          ...ACTION_BUTTON_SX,
          bgcolor: bg,
          '&:hover': { bgcolor: bg, opacity: 0.85 },
        }}
      >
        <Box
          component="img"
          src={src}
          alt=""
          aria-hidden
          sx={{ width, height, display: 'block' }}
        />
      </IconButton>
    </Tooltip>
  )
}

function DirectInstrumentMutateActions({
  item,
  canMutate,
  uiStatus,
  onPause,
  onStop,
  onResume,
}: Readonly<{
  item: IQuickInstrumentListItem
  canMutate: boolean
  uiStatus: DirectInstrumentUiStatus
  onPause: (item: IQuickInstrumentListItem) => void
  onStop: (item: IQuickInstrumentListItem) => void
  onResume: (item: IQuickInstrumentListItem) => void
}>) {
  if (canMutate && uiStatus === 'Live') {
    return (
      <>
        <FigmaActionIcon
          label="Pause instrument"
          bg="rgba(245, 158, 11, 0.2)"
          src={pauseFillIcon}
          width={13}
          height={13}
          onClick={() => onPause(item)}
          testId={`direct-instrument-pause-${item.id}`}
        />
        <FigmaActionIcon
          label="Stop instrument"
          bg="#fff0f0"
          src={stopFillIcon}
          width={18}
          height={18}
          onClick={() => onStop(item)}
          testId={`direct-instrument-stop-${item.id}`}
        />
      </>
    )
  }
  if (canMutate && uiStatus === 'Paused') {
    return (
      <>
        <ActionIcon
          label="Resume instrument"
          tone={{ bg: 'rgba(22, 163, 74, 0.12)', color: SUCCESS }}
          onClick={() => onResume(item)}
          testId={`direct-instrument-resume-${item.id}`}
        >
          <PlayArrowIcon sx={{ fontSize: 15 }} />
        </ActionIcon>
        <FigmaActionIcon
          label="Stop instrument"
          bg="#fff0f0"
          src={stopFillIcon}
          width={18}
          height={18}
          onClick={() => onStop(item)}
          testId={`direct-instrument-stop-${item.id}`}
        />
      </>
    )
  }
  if (uiStatus === 'Completed') {
    return (
      <ActionIcon
        label="Completed"
        tone={{ bg: 'rgba(22, 163, 74, 0.12)', color: SUCCESS }}
        onClick={() => undefined}
        testId={`direct-instrument-completed-${item.id}`}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
      </ActionIcon>
    )
  }
  return null
}

function DirectInstrumentCardToolbar({
  item,
  canMutate,
  uiStatus,
  shareUrl,
  linkCopied,
  onCopyShareLink,
  onPause,
  onStop,
  onResume,
  onNavigate,
}: Readonly<{
  item: IQuickInstrumentListItem
  canMutate: boolean
  uiStatus: DirectInstrumentUiStatus
  shareUrl: string | null | undefined
  linkCopied: boolean
  onCopyShareLink: () => void
  onPause: (item: IQuickInstrumentListItem) => void
  onStop: (item: IQuickInstrumentListItem) => void
  onResume: (item: IQuickInstrumentListItem) => void
  onNavigate: (path: string) => void
}>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '7px' }}>
      {uiStatus === 'Draft' ? (
        <>
          <ActionIcon
            label="View draft"
            tone={{ bg: 'rgba(217, 119, 6, 0.12)', color: WARNING }}
            onClick={() => onNavigate(instrumentBuilderPath(item.id))}
            testId={`direct-instrument-view-draft-${item.id}`}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />
          </ActionIcon>
          {canMutate ? (
            <ActionIcon
              label="Continue publish"
              tone={{ bg: 'rgba(37, 99, 235, 0.1)', color: PRIMARY }}
              onClick={() => onNavigate(targetAudiencePath(item.id))}
              testId={`direct-instrument-continue-publish-${item.id}`}
            >
              <ChevronRightIcon sx={{ fontSize: 15 }} />
            </ActionIcon>
          ) : null}
        </>
      ) : (
        <>
          {shareUrl ? (
            <ActionIcon
              label={linkCopied ? 'Link copied' : 'Copy Sign-up Survey Link'}
              tone={
                linkCopied
                  ? { bg: 'rgba(22, 163, 74, 0.12)', color: SUCCESS }
                  : { bg: '#c7ebf9', color: '#008cd0' }
              }
              onClick={onCopyShareLink}
              testId={`direct-instrument-copy-link-${item.id}`}
            >
              {linkCopied ? (
                <CheckIcon sx={{ fontSize: 15 }} data-testid={`direct-instrument-link-copied-${item.id}`} />
              ) : (
                <LinkIcon sx={{ fontSize: 15 }} />
              )}
            </ActionIcon>
          ) : null}
          <FigmaActionIcon
            label="View results"
            bg="#c7ebf9"
            src={dashboardFillIcon}
            width={16}
            height={16}
            onClick={() => onNavigate(instrumentDashboardPath(item.id))}
            testId={`direct-instrument-view-results-${item.id}`}
          />
        </>
      )}

      <DirectInstrumentMutateActions
        item={item}
        canMutate={canMutate}
        uiStatus={uiStatus}
        onPause={onPause}
        onStop={onStop}
        onResume={onResume}
      />
    </Box>
  )
}

function MetricTile({
  label,
  value,
  bgcolor,
  borderColor,
}: Readonly<{
  label: string
  value: string
  bgcolor: string
  borderColor: string
}>) {
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        height: 70,
        display: 'flex',
        alignItems: 'center',
        pl: '11px',
        pr: '16px',
        borderRadius: '8px',
        bgcolor,
        border: `1px solid ${borderColor}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '32px',
            color: '#0b1c30',
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 500,
            lineHeight: '14px',
            letterSpacing: '0.12px',
            color: '#3f4850',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

function MetaField({
  label,
  value,
  valueColor,
}: Readonly<{ label: string; value: string; valueColor: string }>) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '9px',
          fontWeight: 400,
          lineHeight: '14px',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: META_LABEL,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: '24px',
          color: valueColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export function DirectInstrumentCard({
  item,
  canMutate,
  onPause,
  onStop,
  onResume,
  onEditLive,
}: Props) {
  const navigate = useNavigate()
  const viewer = useSessionStore((s) => s.user)
  const uiStatus = mapApiStatusToUiStatus(item.status)
  const pill = DIRECT_INSTRUMENT_STATUS_PILL[uiStatus]
  const pauseReason = pauseReasonLabel(item)
  const createdByLabel = displayCreatedBy(item, viewer)
  const [linkCopied, setLinkCopied] = useState(false)
  const shareUrl =
    item.isSignupSurvey === true
      ? resolveSignupSurveyShareUrl({
          instrumentId: item.id,
          publicSurveyUrl: item.publicSurveyUrl,
        })
      : null
  const showEditLive = onEditLive != null && (uiStatus === 'Live' || uiStatus === 'Paused')
  const signupSurvey = item.isSignupSurvey === true
  const metricValues: Record<(typeof METRIC_TILES)[number]['key'], string> = {
    budget: formatUsdAmount(resolveBudgetUsedUsd(item)),
    reward: formatUsdAmount(item.rewardPerResponse),
    responses: String(Math.max(0, item.responseCount ?? 0)),
  }

  useEffect(() => {
    if (!linkCopied) return
    const timerId = globalThis.setTimeout(() => {
      setLinkCopied(false)
    }, LINK_COPIED_RESET_MS)
    return () => {
      globalThis.clearTimeout(timerId)
    }
  }, [linkCopied])

  const handleCopyShareLink = () => {
    if (!shareUrl) return
    void navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true)
    })
  }

  return (
    <Box
      component="article"
      data-testid={`direct-instrument-card-${item.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: '15px 16px 16px',
        borderRadius: '10px',
        bgcolor: '#fff',
        border: '1px solid #e2e8f0',
        ...libraryCardHoverSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              minWidth: 0,
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22.5px',
              color: TITLE,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.name}
          </Typography>
          <Box
            component="span"
            data-testid={
              signupSurvey
                ? `direct-instrument-signup-pill-${item.id}`
                : `direct-instrument-regular-pill-${item.id}`
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              height: 26,
              px: '16px',
              borderRadius: '999px',
              bgcolor: signupSurvey ? '#e6f1fb' : '#e5def9',
              color: signupSurvey ? '#008cd0' : '#483584',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {signupSurvey ? SIGN_UP_SURVEY_LABEL : REGULAR_SURVEY_LABEL}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '6px' }}>
          {showEditLive && onEditLive ? (
            <ActionIcon
              label="Edit live instrument"
              tone={{ bg: '#c7ebf9', color: '#008cd0' }}
              onClick={() => onEditLive(item)}
              testId={`direct-instrument-edit-live-${item.id}`}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </ActionIcon>
          ) : null}
          <Box
            component="span"
            data-testid={`direct-instrument-status-${item.id}`}
            sx={{
              ...STATUS_PILL_SX,
              borderColor: pill.border,
              bgcolor: pill.bg,
              color: pill.color,
            }}
          >
            {uiStatus}
          </Box>
          {pauseReason ? (
            <Box
              component="span"
              data-testid={`direct-instrument-pause-reason-${item.id}`}
              sx={{
                ...STATUS_PILL_SX,
                px: '12px',
                borderColor: PAUSE_REASON_PILL.border,
                bgcolor: PAUSE_REASON_PILL.bg,
                color: PAUSE_REASON_PILL.color,
              }}
            >
              {pauseReason}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ mt: '16px', display: 'flex', gap: '10px' }}>
        {METRIC_TILES.map((tile) => (
          <MetricTile
            key={tile.key}
            label={tile.label}
            value={metricValues[tile.key]}
            bgcolor={tile.bgcolor}
            borderColor={tile.borderColor}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          minHeight: 58,
          px: '16px',
          borderRadius: '8px',
          bgcolor: '#eef9ff',
          border: '1px solid #e1e7f3',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Box
            component="img"
            src={calendarEventFillIcon}
            alt=""
            aria-hidden
            sx={{ width: 12, height: 12, display: 'block', flexShrink: 0 }}
          />
          <MetaField
            label="Created Date"
            value={formatCreatedDate(item.createdAt)}
            valueColor={CREATED_DATE}
          />
        </Box>
        <MetaField label="Created By" value={createdByLabel} valueColor={CREATED_BY} />
        <Box sx={{ ml: 'auto', flexShrink: 0 }}>
          <DirectInstrumentCardToolbar
            item={item}
            canMutate={canMutate}
            uiStatus={uiStatus}
            shareUrl={shareUrl}
            linkCopied={linkCopied}
            onCopyShareLink={handleCopyShareLink}
            onPause={onPause}
            onStop={onStop}
            onResume={onResume}
            onNavigate={navigate}
          />
        </Box>
      </Box>
    </Box>
  )
}
